"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectItem } from "@/components/ui/select";
import { authClient } from "@/lib/auth-client";
import { FacultySelect } from "@/components/faculty-select";
import { UserAPIService } from "@/lib/user-api.service";
import { Toast, getSafeErrorMessage } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { ROLE_LABELS, ROLE_OPTIONS } from "@/lib/roles";
import { ModalShell } from "@/components/ui/modal-shell";

interface CreateUserModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function CreateUserModal({ isOpen, onClose }: CreateUserModalProps) {
	const [formData, setFormData] = useState({
		name: "",
		email: "",
		password: "",
		role: "user" as "user" | "admin" | "manager",
		MaCB: "",
		faculty_code: "",
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState("");

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError("");
		setLoading(true);

		try {
			// Khoa cố tình không nằm trong `data` này. Guard ở tầng Prisma chỉ chặn
			// `update`, nên `faculty_code` gửi kèm lúc tạo sẽ được ghi thẳng, bỏ qua
			// việc đối chiếu với catalog khoa. Gán khoa đi qua endpoint riêng bên dưới.
			const { data, error: createError } = await authClient.admin.createUser({
				name: formData.name,
				email: formData.email,
				password: formData.password,
				role: formData.role as any, // Cast to any to support manager role
				data: {
					MaCB: formData.MaCB || undefined,
					// Không phải cờ trang trí. Better Auth chặn việc gắn tài khoản Google
					// vào một user row có `emailVerified: false` (`requireLocalEmailVerified`,
					// mặc định bật) — nên bỏ trống ở đây là tạo ra tài khoản đăng nhập được
					// bằng mật khẩu nhưng vĩnh viễn không đăng nhập được bằng Google, và
					// người dùng chỉ thấy một lỗi 401 không giải thích được.
					//
					// Đặt `true` ở đây an toàn vì không ai tự đăng ký được: cả
					// `emailAndPassword.disableSignUp` lẫn `oneTap.disableSignup` đều bật,
					// nên mọi row đều do admin tạo, và chính thao tác của admin là lời xác
					// nhận quyền sở hữu email mà bước verify lẽ ra phải chứng minh.
					// `scripts/init-users.ts` đặt cùng giá trị này vì cùng lý do.
					emailVerified: true,
				},
			});

			if (createError) {
				// `authClient` trả object thường, không phải `Error` — `String(...)` ở đây
				// sẽ hiện "[object Object]" cho người dùng.
				setError(getSafeErrorMessage(createError));
				return;
			}

			if (data) {
				// Tài khoản đã tồn tại từ đây. Gán khoa hỏng thì không xoá nó đi: tài
				// khoản không có khoa chưa xem được dữ liệu của khoa nào, nên đây là
				// thiếu cấu hình chứ không phải quyền bị nới ra — và một lệnh xoá thêm
				// cũng có thể hỏng nốt.
				if (formData.faculty_code) {
					try {
						await UserAPIService.updateFacultyCode(data.user.id, formData.faculty_code);
					} catch (facultyError) {
						// Lý do đã được `AuthenticatedAPIClient` báo bằng toast lỗi riêng.
						// Toast này chỉ nói phần nó không biết: tài khoản vẫn được tạo.
						console.error("Failed to assign faculty on create:", facultyError);
						Toast.info(
							`Đã tạo tài khoản ${formData.name}`,
							"Chưa gán được khoa — mở Sửa để gán lại.",
						);
						resetForm();
						onClose();
						return;
					}
				}

				Toast.success(`Đã tạo tài khoản ${formData.name} thành công!`);
				resetForm();
				onClose();
			}
		} catch (error: any) {
			console.error("Failed to create user:", error);
			setError(getSafeErrorMessage(error));
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		setFormData({
			name: "",
			email: "",
			password: "",
			role: "user",
			MaCB: "",
			faculty_code: "",
		});
	};

	const handleInputChange = (field: string, value: string) => {
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	return (
		<ModalShell
			isOpen={isOpen}
			onClose={onClose}
			title="Thêm người dùng mới"
			onSubmit={handleSubmit}
			footer={
				<div className="flex gap-3">
					<Button
						type="button"
						variant="outline"
						onClick={onClose}
						disabled={loading}
						className="flex-1"
					>
						Hủy
					</Button>
					<Button type="submit" disabled={loading} className="flex-1">
						{loading ? (
							<>
								<Loader2 className="mr-2 h-4 w-4 animate-spin" />
								Đang tạo...
							</>
						) : (
							"Tạo người dùng"
						)}
					</Button>
				</div>
			}
		>
			<div className="space-y-4">
				{error && (
					<div className="bg-red-50 border border-red-200 rounded-md p-3 dark:bg-red-950 dark:border-red-800">
						<p className="text-red-800 dark:text-red-300 text-sm">{error}</p>
					</div>
				)}

				<div>
					<label className="block text-sm font-medium text-muted-foreground mb-1">
						Họ tên <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<Input
						type="text"
						value={formData.name}
						onChange={(e) => handleInputChange("name", e.target.value)}
						placeholder="Nhập họ tên"
						required
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-muted-foreground mb-1">
						Email <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<Input
						type="email"
						value={formData.email}
						onChange={(e) => handleInputChange("email", e.target.value)}
						placeholder="Nhập email"
						required
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-muted-foreground mb-1">
						Mật khẩu <span className="text-red-500 dark:text-red-400">*</span>
					</label>
					<Input
						type="password"
						value={formData.password}
						onChange={(e) => handleInputChange("password", e.target.value)}
						placeholder="Nhập mật khẩu"
						required
						minLength={6}
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-muted-foreground mb-1">Mã CB</label>
					<Input
						type="text"
						value={formData.MaCB}
						onChange={(e) => handleInputChange("MaCB", e.target.value)}
						placeholder="Nhập mã CB (tùy chọn)"
					/>
				</div>

				<div>
					<label className="block text-sm font-medium text-muted-foreground mb-1">Quyền</label>
					<Select value={formData.role} onChange={(e) => handleInputChange("role", e.target.value)}>
						{ROLE_OPTIONS.map((value) => (
							<SelectItem key={value} value={value}>
								{ROLE_LABELS[value]}
							</SelectItem>
						))}
					</Select>
				</div>

				<div>
					<label
						htmlFor="create-user-faculty"
						className="block text-sm font-medium text-muted-foreground mb-1"
					>
						Khoa
					</label>
					<FacultySelect
						id="create-user-faculty"
						value={formData.faculty_code}
						onChange={(code) => handleInputChange("faculty_code", code)}
						disabled={loading}
					/>
				</div>
			</div>
		</ModalShell>
	);
}
